'use client'

import { useRouter } from 'next/navigation'
import { Card } from '@/components/ui/card'
import { TriangleAlert, Mail, Lock, Unlock, ChevronDown, CheckCircle2, AlertCircle, MapPin } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'

interface Network {
  id: string
  name: string
}

interface AlertSummaryData {
  status: 'active' | 'all_clear' | 'no_alerts'
  high: number
  medium: number
  resolved: number
  top_alert_types: string[]
  dominant_network?: string
}

interface KpiData {
  high: number
  medium: number
  resolved: number
  alert_summary?: string
  alert_summary_data?: AlertSummaryData
  by_network?: Record<string, {
    name: string
    high: number
    medium: number
    resolved: number
  }>
}

interface KpiStatsProps {
  data: KpiData
  /** When true, only High and Resolved cards are shown (SOC source has no medium bucket). */
  hideMedium?: boolean
  networks?: Network[]
  selectedNetworkIds?: string[]
  onNetworkFilterChange?: (networkIds: string[]) => void
  filterLocked?: boolean
  onLockToggle?: () => void
}

export function KpiStats({ 
  data, 
  hideMedium = false,
  networks = [],
  selectedNetworkIds = [],
  onNetworkFilterChange,
  filterLocked = false,
  onLockToggle
}: KpiStatsProps) {
  const router = useRouter()
  const { t } = useLanguage()
  const handleCardClick = () => {
    router.push('/insights')
  }

  const handleNetworkToggle = (networkId: string) => {
    if (!onNetworkFilterChange) return
    
    if (networkId === 'all') {
      onNetworkFilterChange([])
    } else {
      const isSelected = selectedNetworkIds.includes(networkId)
      if (isSelected) {
        onNetworkFilterChange(selectedNetworkIds.filter(id => id !== networkId))
      } else {
        onNetworkFilterChange([...selectedNetworkIds, networkId])
      }
    }
  }

  const isAllNetworks = selectedNetworkIds.length === 0
  const selectedNetworkNames = isAllNetworks 
    ? t.kpi.allNetworks 
    : selectedNetworkIds.length === 1
      ? networks.find(n => n.id === selectedNetworkIds[0])?.name || '1 network'
      : `${selectedNetworkIds.length} networks`

  return (
    <div className="space-y-4">
      {/* Filter Bar */}
      {networks.length > 0 && onNetworkFilterChange && (
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="bg-[#F5F5F5] border-0 text-[#676767] hover:bg-[#EFEFEF]"
              >
                {selectedNetworkNames} <ChevronDown className="ml-2 h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto">
              <DropdownMenuCheckboxItem
                checked={isAllNetworks}
                onCheckedChange={() => handleNetworkToggle('all')}
              >
                {t.kpi.allNetworks}
              </DropdownMenuCheckboxItem>
              {networks.map(network => (
                <DropdownMenuCheckboxItem
                  key={network.id}
                  checked={selectedNetworkIds.includes(network.id)}
                  onCheckedChange={() => handleNetworkToggle(network.id)}
                >
                  {network.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {onLockToggle && (
            <Button
              variant="outline"
              size="sm"
              className="bg-[#F5F5F5] border-0 text-[#676767] hover:bg-[#EFEFEF]"
              onClick={onLockToggle}
              title={filterLocked ? t.kpi.unlockFilter : t.kpi.lockFilter}
            >
              {filterLocked ? (
                <Lock className="h-4 w-4" />
              ) : (
                <Unlock className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className={hideMedium ? 'grid grid-cols-2 gap-6' : 'grid grid-cols-3 gap-6'}>
        <Card 
          className="p-5 border-0 bg-gradient-to-br from-[#EEF2FF] to-white cursor-pointer transition-transform hover:scale-[1.02] hover:shadow-md"
          onClick={handleCardClick}
        >
          <div className="flex flex-col">
            <p className="text-[14px] font-normal text-[#313131] mb-2">{t.kpi.highPriority}</p>
            <h3 className="text-[20px] font-semibold text-[#313131]">{data.high.toLocaleString()}</h3>
          </div>
        </Card>

        {!hideMedium && (
        <Card 
          className="p-5 border-0 bg-[#FBFBFB] cursor-pointer transition-transform hover:scale-[1.02] hover:shadow-md"
          onClick={handleCardClick}
        >
          <div className="flex justify-between">
            <div className="flex flex-col">
              <p className="text-[14px] font-normal text-[#6D6D6D] mb-2">{t.kpi.mediumPriority}</p>
              <h3 className="text-[20px] font-semibold text-[#313131]">{data.medium.toLocaleString()}</h3>
            </div>
            <div className="h-6 w-6 bg-[#FFEDDF] rounded flex items-center justify-center">
              <TriangleAlert className="h-[14px] w-[14px] text-[#F58139]" />
            </div>
          </div>
        </Card>
        )}

        <Card 
          className="p-5 border-0 bg-[#FBFBFB] cursor-pointer transition-transform hover:scale-[1.02] hover:shadow-md"
          onClick={handleCardClick}
        >
          <div className="flex justify-between">
            <div className="flex flex-col">
              <p className="text-[14px] font-normal text-[#313131] mb-2">{t.kpi.resolved}</p>
              <h3 className="text-[20px] font-semibold text-[#313131]">{data.resolved}</h3>
            </div>
            <div className="h-6 w-6 bg-[#E9FFE9] rounded flex items-center justify-center">
              <Mail className="h-[14px] w-[14px] text-[#24B277]" />
            </div>
          </div>
        </Card>
      </div>

      {/* Alert Summary */}
      {(data.alert_summary_data || data.alert_summary) && (() => {
        const rawSd = data.alert_summary_data

        // Merge backend structural data (top_alert_types, dominant_network) with the
        // source-filter-aware counts from kpiData so counts always match the KPI cards
        // and respect both the selected time range and the All/NOC/SOC filter.
        const sd = rawSd ? {
          ...rawSd,
          high: data.high,
          medium: data.medium,
          resolved: data.resolved,
          status: (data.high > 0 || data.medium > 0)
            ? ('active' as const)
            : data.resolved > 0
            ? ('all_clear' as const)
            : ('no_alerts' as const),
        } : undefined

        // --- All Clear ---
        if (sd?.status === 'all_clear') {
          return (
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg px-4 py-3 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-[#16A34A] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-[#15803D]">
                    {t.kpi.summaryLabel}
                  </span>
                  <span className="text-[11px] font-medium text-[#16A34A] bg-[#DCFCE7] px-2 py-0.5 rounded-full">
                    {t.kpi.summaryStatusAllClear}
                  </span>
                </div>
                <p className="text-[13px] text-[#166534] mt-1">
                  {t.kpi.summaryAllClear.replace('{resolved}', String(sd.resolved))}
                </p>
              </div>
            </div>
          )
        }

        // --- No Alerts ---
        if (sd?.status === 'no_alerts') {
          return (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-4 py-3 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-[#9CA3AF] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]">
                    {t.kpi.summaryLabel}
                  </span>
                  <span className="text-[11px] font-medium text-[#6B7280] bg-[#F3F4F6] px-2 py-0.5 rounded-full">
                    {t.kpi.summaryStatusNoAlerts}
                  </span>
                </div>
                <p className="text-[13px] text-[#6B7280] mt-1">{t.kpi.summaryNoAlerts}</p>
              </div>
            </div>
          )
        }

        // --- Active Alerts (rich card) ---
        if (sd?.status === 'active') {
          return (
            <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-lg px-4 py-3">
              {/* Header */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-semibold uppercase tracking-wide text-[#374151]">
                  {t.kpi.summaryLabel}
                </span>
                <span className="flex items-center gap-1.5 text-[11px] font-medium text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] px-2 py-0.5 rounded-full">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#DC2626] animate-pulse" />
                  {t.kpi.summaryStatusActive}
                </span>
              </div>

              {/* Counts row */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2">
                {sd.high > 0 && (
                  <span className="text-[13px] font-semibold text-[#DC2626]">
                    {t.kpi.summaryHighPrefix.replace('{count}', String(sd.high))}
                  </span>
                )}
                {sd.medium > 0 && (
                  <span className="text-[13px] font-medium text-[#D97706]">
                    {t.kpi.summaryMediumPrefix.replace('{count}', String(sd.medium))}
                  </span>
                )}
                {sd.resolved > 0 && (
                  <span className="text-[13px] text-[#16A34A]">
                    {t.kpi.summaryResolvedSuffix.replace('{count}', String(sd.resolved))}
                  </span>
                )}
              </div>

              {/* Alert type chips */}
              {sd.top_alert_types.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mb-2">
                  <span className="text-[11px] text-[#9CA3AF]">{t.kpi.summaryAlertTypes}</span>
                  {sd.top_alert_types.map((type) => (
                    <span
                      key={type}
                      className="text-[11px] font-medium bg-[#EFF6FF] text-[#3B82F6] border border-[#BFDBFE] px-2 py-0.5 rounded-full capitalize"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              )}

              {/* Dominant network */}
              {sd.dominant_network && (
                <div className="flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3 text-[#6B7280]" />
                  <span className="text-[12px] text-[#6B7280]">
                    {t.kpi.summaryAffectedNetwork.replace('{network}', sd.dominant_network)}
                  </span>
                </div>
              )}
            </div>
          )
        }

        // --- Fallback: plain string summary ---
        return (
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-4 py-3">
            <p className="text-[14px] text-[#374151]">{data.alert_summary}</p>
          </div>
        )
      })()}
    </div>
  )
}

